import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Lifebuoy } from "@phosphor-icons/react";
import { AdminHeader, LiveIndicator } from "@/components/admin/parts";
import { TicketCard } from "@/components/admin/tickets/TicketCard";
import { adminApi } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";

const TABS = [
  { key: "in_progress", label: "In Progress", testid: "ticket-tab-in-progress" },
  { key: "completed", label: "Completed", testid: "ticket-tab-completed" },
  { key: "all", label: "All", testid: "ticket-tab-all" },
];

export default function AdminSupportTickets() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("q") ? "all" : "in_progress");
  const [q, setQ] = useState(params.get("q") || "");
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/support-tickets", { params: { status, q } }).then(({ data }) => setRows(data)).catch(() => setRows((r) => r || []));
  }, [status, q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  usePolling(load, 15000);

  const changed = (t) => setRows((prev) => (status !== "all" && t.status !== status ? prev.filter((x) => x.id !== t.id) : prev.map((x) => (x.id === t.id ? t : x))));
  const deleted = (id) => setRows((prev) => prev.filter((x) => x.id !== id));

  return (
    <div>
      <AdminHeader title="Support Tickets" subtitle="Technical support requests from Nick's chat and the Contact Support page" actions={<LiveIndicator />} />
      <div className="p-8 space-y-6 max-w-[1400px]">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-[#0F0F0F] border border-neutral-800 rounded-full p-1">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setStatus(t.key)} data-testid={t.testid}
                className={`text-xs px-3.5 py-1.5 rounded-full transition-colors ${status === t.key ? "bg-[#73bf44] text-[#050505] font-semibold" : "text-neutral-400 hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-[#0F0F0F] border border-neutral-800 rounded-full px-4 py-2 w-80 focus-within:border-[#73bf44] transition-colors">
            <MagnifyingGlass size={15} className="text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="tickets-search" placeholder="Search reference, name, chassis, phone…"
              className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600" />
          </div>
          {rows && <span className="text-xs text-neutral-500" data-testid="tickets-count">{rows.length} ticket{rows.length === 1 ? "" : "s"}</span>}
        </div>
        {!rows ? (
          <div className="text-neutral-600">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3" data-testid="tickets-empty">
            <Lifebuoy size={32} className="text-neutral-700" weight="duotone" />
            <div className="text-sm text-neutral-500">{q.trim() ? `No tickets match “${q.trim()}”.` : status === "in_progress" ? "No tickets waiting — nice work." : "No tickets found."}</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="tickets-list">
            {rows.map((t) => <TicketCard key={t.id} t={t} onChanged={changed} onDeleted={deleted} />)}
          </div>
        )}
      </div>
    </div>
  );
}
